import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ShareRow from '../../components/ShareRow/ShareRow';
import { SIGNS } from '../../data/astro/signs';
import { matchFor, pairPath, scoreTone } from '../../data/compatibility';
import { Faq, faqJsonLd } from './parts';
import '../Astro/Astro.css';
import './Compatibility.css';

const FAQ = [
  {
    q: 'How is zodiac compatibility worked out?',
    a: 'Each pairing is read from the two Sun signs: their elements (fire, earth, air, water), their modalities (cardinal, fixed, mutable), and their ruling planets. Those decide how two people tend to handle affection, conflict, pace, and trust. The scores sum that up across six parts of a relationship.',
  },
  {
    q: 'Why are "Aries man, Cancer woman" and "Cancer man, Aries woman" different pages?',
    a: 'The scores for a pair of signs are the same either way, but how the match plays out changes with who is who. A Cancer man shows his sign differently from a Cancer woman in love, so each direction gets its own reading and its own advice.',
  },
  {
    q: 'What if our signs are a low match?',
    a: 'A low score means more translation, not a doomed relationship. Plenty of couples with hard Sun sign matches do beautifully, because the Moon, Venus, and rising signs matter too. Each page shows exactly where the friction is and what helps.',
  },
  {
    q: 'Is Sun sign compatibility the whole story?',
    a: 'No. It is the quickest view. For the full picture, compare both birth charts: Venus shows how each person loves, the Moon shows emotional needs, and Mars shows desire. Birth chart compatibility on NowTarot does that for free.',
  },
  {
    q: 'We are a same-sex couple. Which page should we read?',
    a: 'Open either of your signs and read the match summary for your pair, then read both gendered pages for your two signs. The scores are identical, and each page has insights that fit whoever leads with that sign’s energy.',
  },
];

const CompatibilityHub = () => {
  const navigate = useNavigate();
  const [man, setMan] = useState('');
  const [woman, setWoman] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();
    if (man && woman) navigate(pairPath(man, woman));
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Zodiac Love Compatibility Calculator',
        url: 'https://nowtarot.com/zodiac-compatibility/',
        applicationCategory: 'LifestyleApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        description: 'Free zodiac love compatibility for every sign pairing, with scores for love, trust, friendship, intimacy, and marriage.',
      },
      faqJsonLd(FAQ),
    ],
  };

  return (
    <div className="astro-page compat-page">
      <Helmet>
        <title>Zodiac Compatibility: Love Match for Every Sign | NowTarot</title>
        <meta name="description" content="Free zodiac love compatibility for all 144 man and woman sign pairings. See love, trust, friendship, intimacy, and marriage scores with an honest, detailed reading." />
        <link rel="canonical" href="https://nowtarot.com/zodiac-compatibility/" />
        <meta property="og:title" content="Zodiac Compatibility: Love Match for Every Sign" />
        <meta property="og:description" content="How compatible are your signs? Free love readings for every pairing." />
        <meta property="og:url" content="https://nowtarot.com/zodiac-compatibility/" />
        <meta property="og:image" content="https://nowtarot.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main>
        <p className="astro-eyebrow">Tool · Free · Astrology</p>
        <h1>Zodiac <em>love</em> compatibility</h1>
        <p className="astro-lede">
          Pick his sign and her sign for a full, honest reading of the match: where it flows, where it clashes, and
          what each of you can do about it.
        </p>

        <section className="astro-card">
          <form className="astro-form compat-form" onSubmit={handleSubmit}>
            <label>
              <span>His sign</span>
              <select value={man} onChange={(event) => setMan(event.target.value)} required>
                <option value="">Choose…</option>
                {SIGNS.map((sign) => <option key={sign.slug} value={sign.slug}>{sign.glyph} {sign.name}</option>)}
              </select>
            </label>
            <label>
              <span>Her sign</span>
              <select value={woman} onChange={(event) => setWoman(event.target.value)} required>
                <option value="">Choose…</option>
                {SIGNS.map((sign) => <option key={sign.slug} value={sign.slug}>{sign.glyph} {sign.name}</option>)}
              </select>
            </label>
            <button className="astro-button" type="submit">See our match</button>
          </form>
          <p className="astro-muted compat-hint">
            Not sure of a sign? The <Link to="/big-three-calculator/">Big Three calculator</Link> finds Sun, Moon, and
            rising from a birthday. For the deepest view, try <Link to="/birth-chart-compatibility/">birth chart compatibility</Link>.
          </p>
        </section>

        <section className="astro-section">
          <h2>Choose a sign</h2>
          <div className="compat-signs">
            {SIGNS.map((sign) => (
              <Link key={sign.slug} to={`/zodiac-compatibility/${sign.slug}/`} className="compat-sign-tile">
                <span className="astro-glyph" aria-hidden="true">{sign.glyph}</span>
                <strong>{sign.name}</strong>
                <span>{sign.dates}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="astro-section">
          <h2>Every match at a glance</h2>
          <p className="astro-muted">Rows are his sign, columns are her sign. Tap a score to read that match.</p>
          <div className="compat-grid-wrap">
            <table className="compat-grid">
              <thead>
                <tr>
                  <th scope="col"><span className="compat-visually-hidden">His sign by her sign</span></th>
                  {SIGNS.map((sign) => (
                    <th key={sign.slug} scope="col" title={`${sign.name} woman`}>
                      <span aria-hidden="true">{sign.glyph}</span>
                      <span className="compat-grid-name">{sign.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SIGNS.map((him) => (
                  <tr key={him.slug}>
                    <th scope="row" title={`${him.name} man`}>
                      <span aria-hidden="true">{him.glyph}</span>
                      <span className="compat-grid-name">{him.name}</span>
                    </th>
                    {SIGNS.map((her) => {
                      const { overall } = matchFor(him.slug, her.slug);
                      return (
                        <td key={her.slug} className={scoreTone(overall)}>
                          <Link to={pairPath(him.slug, her.slug)} aria-label={`${him.name} man and ${her.name} woman: ${overall}%`}>
                            {overall}
                          </Link>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="astro-card compat-share-card">
          <h2>Send this to someone you like</h2>
          <p>Let them look up your match. It’s free, and it might start a good conversation.</p>
          <ShareRow url="/zodiac-compatibility/" text="How compatible are our signs? Look us up:" />
        </section>

        <Faq items={FAQ} title="Zodiac compatibility FAQ" />
      </main>
      <Footer />
    </div>
  );
};

export default CompatibilityHub;
